import { Box, LinkBox, LinkOverlay, Tag, Text, useBreakpointValue } from "@chakra-ui/react"
import { useRouter } from "next/router"
import { fhAnalyticsUserActions, pages } from "../../../common/constants/constants"
import { sparePartsAttributes, tabs } from "../../../common/constants/search-constants"
import { useBrandedTranslation } from "../../../common/hooks/useBrandedTranslation"
import sendFhAnalytics from "../../../common/lib/fredhopper"
import { TileProduct, TileSparePart } from "../../../common/types/global"
import { redirectToPage } from "../../../common/utils/router-utils"
import { specialFhValue } from "../../../common/utils/spare-parts-utils"
import CustomImage from "../CustomImage"
import CustomLink from "../CustomLink"
import NewIndicator from "../NewIndicator"
import PhasingOutIndicator from "../PhasingOutIndicator"

type Props = {
  item: Partial<TileProduct> & Partial<TileSparePart>
  hasAnalytics?: boolean
  rid?: { [key: string]: string }
}

/* This is Search results basic view tile */

export const BasicSearchTile = ({ item, hasAnalytics = false, rid }: Props) => {
  const { t } = useBrandedTranslation()
  const router = useRouter()
  const isSpt = item?.id?.startsWith(sparePartsAttributes.sptType) ?? false
  const page = isSpt ? pages.SPARE_PART : pages.PRODUCT
  const itemQuery = isSpt ? "" : item.query ? `?${item.query}` : ""
  const itemHref = `/${page}/${item.id}${itemQuery}`
  const hasTag = item.isNew || item.series
  const newTagTooltipLabel = item.availableFrom
    ? `${t("pdp-newLabelTip", { count: item.articlesCount })} ${item.availableFrom}`
    : ""
  const afterElementContent = useBreakpointValue({ base: "none", xs: '""' })

  const handleProductTileClick = (e: React.MouseEvent<HTMLElement>) => {
    if (hasAnalytics) {
      const isLeftButtonClicked = e.button === 0
      const isRightButtonClicked = e.button === 2
      const isCtrlOrMetaKeyPressed = e.metaKey || e.ctrlKey
      const openInSameTab = isLeftButtonClicked && !isCtrlOrMetaKeyPressed

      if (isRightButtonClicked) return false
      if (openInSameTab) e.preventDefault()

      const itemRid = isSpt ? rid[tabs.SPT] : rid[tabs.PRO]
      const userAction = isSpt ? fhAnalyticsUserActions.SPTCLICK : fhAnalyticsUserActions.PROCLICK

      //wait until analytics is sent as otherwise analytics call is cancelled
      sendFhAnalytics({
        locale: router.locale,
        action: userAction,
        sourceId: itemRid,
        itemId: item.id,
        shouldBeInterrupted: openInSameTab ? true : false,
      }).finally(() => {
        if (openInSameTab) redirectToPage(router, page, item.id, itemQuery)
      })
    }
  }

  return (
    <LinkBox
      as="article"
      _hover={{ "@media (hover: hover) and (pointer: fine)": { color: "brand.500" } }}
      _active={{ color: "brand.700" }}
      pt={{ base: 0, xs: 6 }}
      pb={{ base: 0, xs: 5 }}
      pl={{ base: 0, xs: 6 }}
      pr={{ base: 0, xs: 8 }}
      _after={{
        content: `${afterElementContent}`,
        background: "gray.300",
        position: "absolute",
        top: "0",
        bottom: "0",
        right: "0",
        height: "105",
        width: "1px",
      }}
      mx="0"
      sx={{ "@media (hover: none), (pointer: coarse)": { ".action-button": { display: "inline-block" } } }}
    >
      <CustomLink href={itemHref}>
        <LinkOverlay onClick={handleProductTileClick} onAuxClick={handleProductTileClick}>
          <Box>
            <Box w={{ base: "53", xs: "59" }} h={{ base: "53", xs: "59" }} mb="6" overflow="clip" mx="auto">
              <CustomImage
                src={item.image}
                alt="product image"
                w={{ base: "53", xs: "59" }}
                h={{ base: "53", xs: "59" }}
                mx="auto"
                fit="cover"
              />
            </Box>
            {hasTag && (
              <Box position="relative" mt="-9" mb="3">
                {item.isNew && <NewIndicator tooltipLabel={newTagTooltipLabel} tooltipPlacement="top-start" />}
                {item.series && <Tag variant="series">{item.series}</Tag>}
              </Box>
            )}
            <Text fontSize="2xs" mb={2} color="gray.700">
              {item.category}
            </Text>
            <Text lineHeight={{ base: "1.27", md: "1.33", lg: "1.23" }} fontWeight="bold" fontSize="2xs" mb={2}>
              {item.name}
            </Text>
            {!!item.yearManufactureFrom && (
              <Text fontSize="2xs" color="gray.700" mb={2}>
                {specialFhValue(item.yearManufactureFrom)}-{specialFhValue(item.yearManufactureUntil)}
              </Text>
            )}
            {!!item.articlesCount && (
              <Text fontSize="2xs" color="gray.700">
                {item.articlesCount} {t("pdp-tile-article-unit", { count: item.articlesCount })}
              </Text>
            )}
            {item.isPhasedOut && (
              <Box mt="2">
                <PhasingOutIndicator />
              </Box>
            )}
          </Box>
        </LinkOverlay>
      </CustomLink>
    </LinkBox>
  )
}
